/**
 * Copyright 2023 Design Barn Inc.
 */

import { Button, IconButton } from '@lottiefiles/ds-core';
import { useRouter } from 'next/navigation';
import React, { useCallback } from 'react';

import { GmailIcon, FacebookIcon, GithubIcon, DribbbleIcon, PasswordIcon } from '../icons';

export interface LoginRegisterButtonProps {
  googleButtonText: string;
  onDribbble?: () => Promise<void>;
  onFacebook: () => void;
  onGithub: () => Promise<void>;
  onGoogle: () => Promise<void>;
  passwordLinkHref: string;
}

export default function LoginRegisterButtons({
  googleButtonText,
  onDribbble,
  onFacebook,
  onGithub,
  onGoogle,
  passwordLinkHref,
}: LoginRegisterButtonProps): React.ReactElement {
  const router = useRouter();

  const googleIcon = (
    // eslint-disable-next-line react-perf/jsx-no-jsx-as-prop
    <GmailIcon />
  );

  const facebookIcon = (
    // eslint-disable-next-line react-perf/jsx-no-jsx-as-prop
    <FacebookIcon />
  );

  const githubIcon = (
    // eslint-disable-next-line react-perf/jsx-no-jsx-as-prop
    <GithubIcon />
  );

  const dribbbleIcon = (
    // eslint-disable-next-line react-perf/jsx-no-jsx-as-prop
    <DribbbleIcon />
  );

  const passwordIcon = (
    // eslint-disable-next-line react-perf/jsx-no-jsx-as-prop
    <PasswordIcon />
  );

  const onPassword = useCallback(() => {
    // do nothing
    router.push(passwordLinkHref);
  }, [passwordLinkHref, router]);

  const handleGoogle = useCallback(() => {
    onGoogle();
  }, [onGoogle]);

  const handleGitHub = useCallback(() => {
    onGithub();
  }, [onGithub]);

  const handleDribbble = useCallback(() => {
    if (onDribbble) {
      onDribbble();
    }
  }, [onDribbble]);

  const LoginButton = ({ icon, id, onClick }: { icon: React.ReactNode; id: string; onClick: () => void }): React.ReactElement => (
    <IconButton
      id={id}
      onClick={onClick}
      size="md"
      variant="primary"
      className="flex justify-center border-medium mt-4 h-[40px] rounded-[8px] border-[1px] w-full"
    >
      {icon}
    </IconButton>
  );

  return (
    <>
      <Button
        id="button-google-login"
        onClick={handleGoogle}
        variant="primary"
        size="lg"
        iconLeft={googleIcon}
        className="w-full rounded-[12px] text-base font-normal"
      >
        {googleButtonText}
      </Button>

      <div className="flex justify-between space-x-5">
        <LoginButton icon={facebookIcon} id="button-facebook-login" onClick={onFacebook} />

        <LoginButton icon={githubIcon} id="button-github-login" onClick={handleGitHub} />

        <LoginButton icon={passwordIcon} id="button-password-login" onClick={onPassword} />

        {onDribbble && <LoginButton id="button-dribbble-login" icon={dribbbleIcon} onClick={handleDribbble} />}
      </div>
    </>
  );
}
